import { useCallback, useState } from 'react';

import { isDefined } from '../../utils/variables';
import { AssetManager } from '../../viewer/asset-manager';
import { Design } from '../../viewer/elements/design';
import { Dxf } from '../../viewer/elements/dxf';
import { AssetType, ElementObj } from '../../viewer/elements/element-obj';
import { Geometry } from '../../viewer/elements/geometry';
import { Glb } from '../../viewer/elements/glb';
import { Model } from '../../viewer/elements/model';
import { TerrainModel } from '../../viewer/elements/terrain-model';
import { SubscriptionTopic } from '../../viewer/messaging/pubsub';
import { useSubscribe } from './use-subscribe';

function sortAssets(a: ElementObj, b: ElementObj) {
  if (a.assetType !== b.assetType) {
    const weight = (asset: ElementObj) =>
      asset instanceof TerrainModel
        ? 5
        : asset instanceof Model
          ? 4
          : asset instanceof Dxf || asset instanceof Glb
            ? 3
            : asset instanceof Geometry && !asset.isArea
              ? 2
              : asset instanceof Geometry && asset.isArea
                ? 1
                : asset instanceof Design
                  ? 0
                  : 6;

    const weightA = weight(a);
    const weightB = weight(b);

    return weightA > weightB ? 1 : -1;
  }

  return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
}

function getAssets<T>(filter?: AssetType[]) {
  return AssetManager.instance
    .getAssets()
    .filter((x) => !isDefined(filter) || filter.includes(x.assetType))
    .sort(sortAssets) as T[];
}

export function useAssets<T extends ElementObj = ElementObj>(filter?: AssetType[]) {
  const [assets, setAssets] = useState<T[]>(getAssets(filter));

  const refresh = useCallback(() => {
    const filteredAssets = getAssets<T>(filter);

    if (filteredAssets.every((x, idx) => x === assets[idx])) {
      return;
    }

    setAssets(filteredAssets);
  }, [filter, assets]);

  useSubscribe(SubscriptionTopic.AssetListChanged, () => {
    refresh();
  });

  return { assets };
}
